<template>
  <!-- Floating button to reopen when collapsed -->
  <button
    v-if="isVisible && isCollapsed"
    type="button"
    @click="handleExpand"
    class="theme-editor__reopen-btn"
    title="Open Theme Editor"
  >
    <Cog class="theme-editor__reopen-btn__icon" />
  </button>

  <div v-if="isVisible && !isCollapsed" class="theme-editor">
    <div
      class="theme-editor__background"
      @click="handleCollapse"
      @keydown.enter="handleCollapse"
      @keydown.space="handleCollapse"
      tabindex="0"
      role="button"
      aria-label="Minimize theme editor"
    />

    <div class="theme-editor__panel">
      <button
        type="button"
        @click="handleClose"
        class="theme-editor__panel__close-btn"
        title="Close Theme Editor"
      >
        <Cross class="theme-editor__panel__close-btn__icon" />
      </button>
      <button
        type="button"
        @click="handleCollapse"
        class="theme-editor__panel__minimize-btn"
        title="Minimize Theme Editor"
      >
        <span class="theme-editor__panel__minimize-btn__icon">−</span>
      </button>

      <div class="theme-editor__panel__header">
        <div class="theme-editor__panel__header__subheader">
          Theme Editor
        </div>
        <div class="theme-editor__panel__header__title">
          Customize Theme
        </div>
      </div>

      <div class="theme-editor__panel__content">
        <!-- Primary Color -->
        <ThemeEditorColorInput
          v-model="themeValues.primary"
          label="Primary Color"
          input-id="primaryColor"
          placeholder="#4b9cdd"
          @update:model-value="handleColorChange('primary', $event)"
        />

        <!-- Secondary Color -->
        <ThemeEditorColorInput
          v-model="themeValues.secondary"
          label="Secondary Color"
          input-id="secondaryColor"
          placeholder="#00d6bf"
          @update:model-value="handleColorChange('secondary', $event)"
        />

        <!-- Background Color -->
        <ThemeEditorColorInput
          v-model="themeValues.background"
          label="Background Color"
          input-id="backgroundColor"
          placeholder="#111630"
          :show-checkbox="true"
          :checkbox-value="setBackground"
          checkbox-label="Apply background color"
          checkbox-id="setBackground"
          @update:model-value="handleColorChange('background', $event)"
          @update:checkbox-value="setBackground = $event"
        />

        <!-- Text Color -->
        <ThemeEditorColorInput
          v-model="themeValues.textColor"
          label="Text Color"
          input-id="textColor"
          placeholder="#ffffff"
          @update:model-value="handleColorChange('textColor', $event)"
        />

        <!-- Border Radius -->
        <div class="theme-editor__panel__field">
          <label for="borderRadius" class="theme-editor__panel__field__label">
            Border Radius
          </label>
          <input
            id="borderRadius"
            type="text"
            v-model="themeValues.borderRadius"
            @input="handleBorderRadiusChange"
            class="theme-editor__panel__field__text-input"
            placeholder="1.6rem"
          />
          <p class="theme-editor__panel__field__hint">
            Enter value with unit (e.g., 1.6rem, 16px) or just a number (assumes rem)
          </p>
        </div>

        <!-- Font Family -->
        <div class="theme-editor__panel__field">
          <label for="fontFamily" class="theme-editor__panel__field__label">
            Font Family
          </label>
          <select
            id="fontFamily"
            v-model="themeValues.fontFamily"
            @change="handleFontChange"
            class="theme-editor__panel__field__select"
          >
            <option value="">Default (Rubik)</option>
            <option v-for="font in allowedFonts" :key="font" :value="font">
              {{ font }}
            </option>
          </select>
          <p class="theme-editor__panel__field__hint">
            Select a Google Font to use throughout the application
          </p>
        </div>

        <!-- Reset Button -->
        <div class="theme-editor__panel__actions">
          <button
            type="button"
            @click="handleReset"
            class="theme-editor__panel__actions__reset-btn"
          >
            Reset to Defaults
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, computed, watch, onMounted,
} from 'vue';
import { useRoute } from 'vue-router';
import Cross from '@/components/icons/Cross.vue';
import Cog from '@/components/icons/Cog.vue';
import ThemeEditorColorInput from '@/components/ui/ThemeEditorColorInput.vue';
import useTheme from '@/composables/ui/useTheme';
import useGoogleFonts from '@/composables/ui/useGoogleFonts';

const route = useRoute();
const { setThemeColours, updateTheme } = useTheme();
const { ALLOWED_FONTS } = useGoogleFonts();
const allowedFonts = ref(ALLOWED_FONTS);

const isVisible = computed(() => {
  const { searchParams } = new URL(window.location.href);
  return searchParams.get('themeEditor') === 'true';
});

const isCollapsed = ref(false);
const setBackground = ref(true);

// Default theme values
const DEFAULT_THEME = {
  primary: '#4b9cdd',
  secondary: '#00d6bf',
  background: '#111630',
  textColor: '#ffffff',
  borderRadius: '1.6rem',
  fontFamily: '',
  mode: 'DARK',
};

const themeValues = ref({ ...DEFAULT_THEME });

// Initialize from current CSS variables or defaults
const initializeThemeValues = () => {
  const root = document.documentElement;
  const computedStyle = getComputedStyle(root);

  themeValues.value.primary = computedStyle.getPropertyValue('--primary').trim() || DEFAULT_THEME.primary;
  themeValues.value.secondary = computedStyle.getPropertyValue('--secondary').trim() || DEFAULT_THEME.secondary;
  themeValues.value.background = computedStyle.getPropertyValue('--background').trim() || DEFAULT_THEME.background;
  themeValues.value.textColor = computedStyle.getPropertyValue('--text-color').trim() || DEFAULT_THEME.textColor;
  themeValues.value.borderRadius = computedStyle.getPropertyValue('--border-radius').trim() || DEFAULT_THEME.borderRadius;

  // Extract font family from CSS (remove quotes and fallback fonts)
  const fontFamilyValue = computedStyle.getPropertyValue('--font-family').trim();
  if (fontFamilyValue) {
    const match = fontFamilyValue.match(/^['"]([^'"]+)['"]/);
    themeValues.value.fontFamily = match ? match[1] : '';
  } else {
    themeValues.value.fontFamily = '';
  }

  themeValues.value.mode = root.classList.contains('lightmode') ? 'LIGHT' : 'DARK';
};

const normalizeColor = (color) => {
  if (!color) return null;
  // If it doesn't start with #, add it
  if (!color.startsWith('#')) {
    return `#${color}`;
  }
  return color;
};

const applyTheme = () => {
  setThemeColours(
    {
      primary: themeValues.value.primary,
      secondary: themeValues.value.secondary,
      background: themeValues.value.background,
      textColor: themeValues.value.textColor,
      borderRadius: themeValues.value.borderRadius,
      fontFamily: themeValues.value.fontFamily || null,
    },
    setBackground.value,
  );
};

const handleColorChange = (colorType, value) => {
  const normalizedColor = normalizeColor(value);
  themeValues.value[colorType] = normalizedColor;
  applyTheme();
};

const handleBorderRadiusChange = () => {
  applyTheme();
};

const handleFontChange = () => {
  applyTheme();
};

const handleReset = () => {
  themeValues.value = { ...DEFAULT_THEME };
  setBackground.value = true;
  updateTheme(false);
  applyTheme();
};

const handleCollapse = () => {
  isCollapsed.value = true;
  // Store collapsed state in sessionStorage
  sessionStorage.setItem('themeEditorCollapsed', 'true');
};

const handleExpand = () => {
  isCollapsed.value = false;
  sessionStorage.removeItem('themeEditorCollapsed');
};

const handleClose = () => {
  // Remove themeEditor parameter from URL
  const url = new URL(window.location.href);
  url.searchParams.delete('themeEditor');
  window.history.replaceState({}, '', url);
  // Reset collapsed state
  isCollapsed.value = false;
  sessionStorage.removeItem('themeEditorCollapsed');
};

watch(
  () => route.query.themeEditor,
  () => {
    if (isVisible.value) {
      initializeThemeValues();
      // Check if editor was previously collapsed
      const wasCollapsed = sessionStorage.getItem('themeEditorCollapsed') === 'true';
      if (wasCollapsed) {
        isCollapsed.value = true;
      } else {
        isCollapsed.value = false;
      }
    } else {
      // Reset collapsed state when editor is closed via URL
      isCollapsed.value = false;
      sessionStorage.removeItem('themeEditorCollapsed');
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (isVisible.value) {
    initializeThemeValues();
    // Check if editor was previously collapsed
    const wasCollapsed = sessionStorage.getItem('themeEditorCollapsed') === 'true';
    if (wasCollapsed) {
      isCollapsed.value = true;
    }
  }
});
</script>

<style lang="scss">
.theme-editor {
  position: fixed;
  width: 100vw;
  max-width: 100vw;
  max-height: 100vh;
  height: 100vh;
  opacity: 1;
  overflow: hidden;
  box-sizing: border-box;
  left: 0;
  top: 0;
  z-index: 20;

  &__background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.5;
  }

  &__panel {
    width: 100%;
    max-width: 480px;
    height: 100vh;
    padding: $padding-lg;
    background: var(--settings-overlay-background);
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: $formFieldSpacing;
    position: absolute;
    top: 0;
    right: 0;
    overflow-y: auto;

    @include breakpoint($max-m) {
      max-width: 100%;
    }

    &__close-btn {
      position: absolute;
      top: 2.4rem;
      right: 2.4rem;
      border: none;
      background: none;
      cursor: pointer;
      z-index: 2;

      &__icon {
        g {
          transition: var(--button-icon-transition);

          path {
            stroke: var(--icon-color-default);
          }
        }

        &:hover {
          g {
            opacity: 1;
          }
        }
      }
    }

    &__minimize-btn {
      position: absolute;
      top: 2.4rem;
      right: 6rem;
      border: none;
      background: none;
      cursor: pointer;
      z-index: 2;
      width: 3.2rem;
      height: 3.2rem;
      display: flex;
      align-items: center;
      justify-content: center;

      &__icon {
        font-size: 2.4rem;
        font-weight: 300;
        line-height: 1;
        color: var(--icon-color-default);
        transition: var(--button-icon-transition);
      }

      &:hover {
        .theme-editor__panel__minimize-btn__icon {
          opacity: 1;
        }
      }
    }

    &__header {
      margin-top: 2rem;

      &__subheader {
        font-weight: 500;
        font-size: $font-subhead;
        color: var(--primary);
        margin-bottom: 0.5rem;
      }

      &__title {
        font-weight: 900;
        font-size: $font-sub-jumbo;
        color: var(--text-color);
      }
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: $formFieldSpacing;
    }

    &__field {
      display: flex;
      flex-direction: column;
      gap: 0.8rem;

      &__label {
        font-weight: 500;
        font-size: $font-body;
        color: var(--text-color);
        margin-left: 0.8rem;
      }

      &__input-group {
        display: flex;
        gap: 1rem;
        align-items: center;
      }

      &__color-input {
        width: 60px;
        height: 50px;
        border: 2px solid var(--input-background);
        border-radius: var(--border-radius);
        cursor: pointer;
        background: none;
        padding: 0;
        flex-shrink: 0;

        &::-webkit-color-swatch-wrapper {
          padding: 0;
        }

        &::-webkit-color-swatch {
          border: none;
          border-radius: calc(var(--border-radius) - 2px);
        }
      }

      &__text-input {
        flex: 1;
        padding: 1.75rem;
        background-color: var(--input-background);
        border-radius: var(--border-radius);
        border: 2px solid transparent;
        font-size: $font-input-size;
        color: var(--text-color);

        @include breakpoint($min-m) {
          font-size: $font-body;
        }

        &::placeholder {
          color: var(--input-place-holder-text-color);
          opacity: 1;
        }
      }

      &__select {
        width: 100%;
        padding: 1.75rem;
        background-color: var(--input-background);
        border-radius: var(--border-radius);
        border: 2px solid transparent;
        font-size: $font-input-size;
        color: var(--text-color);
        cursor: pointer;
        @include addBaseHover();

        @include breakpoint($min-m) {
          font-size: $font-body;
        }

        option {
          background-color: var(--settings-overlay-background);
          color: var(--text-color);
        }
      }

      &__checkbox-group {
        display: flex;
        align-items: center;
        gap: 0.8rem;
        margin-left: 0.8rem;
      }

      &__checkbox {
        width: 18px;
        height: 18px;
        cursor: pointer;
        accent-color: var(--primary);
      }

      &__checkbox-label {
        font-size: $font-body;
        color: var(--text-color);
        cursor: pointer;
      }

      &__hint {
        font-size: $font-body-small;
        color: var(--input-place-holder-text-color);
        margin-left: 0.8rem;
        margin-top: -0.4rem;
      }
    }

    &__actions {
      margin-top: 1rem;
      padding-top: 1rem;
      border-top: 1px solid var(--input-background);

      &__reset-btn {
        width: 100%;
        padding: 1.75rem;
        background-color: var(--input-background);
        border-radius: var(--border-radius);
        border: 2px solid transparent;
        font-size: $font-body;
        font-weight: 500;
        color: var(--text-color);
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          background-color: var(--primary);
          color: white;
        }

        &:active {
          transform: scale(0.98);
        }
      }
    }
  }

  &__reopen-btn {
    position: fixed;
    bottom: 2.4rem;
    right: 2.4rem;
    width: 5.6rem;
    height: 5.6rem;
    border-radius: 50%;
    background: var(--primary);
    border: none;
    cursor: pointer;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
    }

    &:active {
      transform: scale(0.95);
    }

    &__icon {
      width: 2.4rem;
      height: 2.4rem;

      g {
        transition: var(--button-icon-transition);

        path {
          stroke: white;
        }
      }
    }
  }
}
</style>
